import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/admin/_layout/inventory/item-prices")({
  component: ItemPricesPage,
});

import { useState } from "react";
import { toast } from "sonner";
import { SidebarInset } from "@/components/ui/sidebar";
import { DashboardHeader } from "@/components/dashboard-header";
import PageHeader from "@/components/reusable-components/header/page-header";
import { PaginationComponent } from "@/components/crud/Pagination/Pagination";
import { PAGE_SIZE_OPTIONS } from "@/lib/constants";
import { usePaginationState } from "@/hooks/ui/usePaginationState";
import { DeleteConfirmDialog } from "@/components/crud/DeleteConfirmDialog/DeleteConfirmDialog";
import { ItemPriceFormSheet } from "@/components/inventory/forms/ItemPriceFormSheet";
import { ItemPriceTable } from "@/components/inventory/tables/ItemPriceTable";
import {
  useFetchItemPrices,
  useItemPriceMutations,
} from "@/hooks/api/inventory/usePricing";
import type { ItemPrice, ItemPricePayload } from "@/types/inventory";

function ItemPricesPage() {
  const { page, setPage, pageSize, handlePageSizeChange } =
    usePaginationState();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [editing, setEditing] = useState<ItemPrice | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ItemPrice | null>(null);

  const itemPricesQuery = useFetchItemPrices({ page, page_size: pageSize });
  const itemPrices = itemPricesQuery.data?.results ?? [];
  const count = itemPricesQuery.data?.count ?? 0;

  const { createItemPrice, updateItemPrice, deleteItemPrice } =
    useItemPriceMutations();

  const isMutating =
    createItemPrice.isPending ||
    updateItemPrice.isPending ||
    deleteItemPrice.isPending;
  const isLoading = itemPricesQuery.isPending || isMutating;
  const isFetching = itemPricesQuery.isFetching || isMutating;

  const handleSubmit = async (data: ItemPricePayload) => {
    if (editing) {
      await updateItemPrice.mutateAsync({ id: editing.id, payload: data });
      toast.success("Price entry updated");
    } else {
      await createItemPrice.mutateAsync(data);
      toast.success("Price entry added");
    }
    setIsSheetOpen(false);
    setEditing(null);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      await deleteItemPrice.mutateAsync({ id: deleteTarget.id });
      toast.success("Price entry deleted");
      setDeleteTarget(null);
    } catch {
      // error toast comes from the mutation's meta.errorMessage
    }
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="space-y-4 p-6 pt-0">
        <PageHeader
          title="Item Prices"
          description="Manage price book prices for inventory items"
          onAdd={() => {
            setEditing(null);
            setIsSheetOpen(true);
          }}
          addLabel="Add Price"
          isLoading={isFetching}
        />

        <ItemPriceTable
          itemPrices={itemPrices}
          isLoading={isLoading}
          onView={(ip) => {
            setEditing(ip);
            setIsViewOnly(true);
            setIsSheetOpen(true);
          }}
          onEdit={(ip) => {
            setEditing(ip);
            setIsViewOnly(false);
            setIsSheetOpen(true);
          }}
          onDelete={(ip) => setDeleteTarget(ip)}
        />

        <PaginationComponent
          count={count}
          page={page}
          onPageChange={(p) => setPage(p)}
          isLoading={isFetching}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
        />

        <ItemPriceFormSheet
          isOpen={isSheetOpen}
          onClose={() => {
            setIsSheetOpen(false);
            setEditing(null);
            setIsViewOnly(false);
          }}
          onSave={handleSubmit}
          items={[]}
          editing={editing}
          isLoading={createItemPrice.isPending || updateItemPrice.isPending}
          isViewOnly={isViewOnly}
        />

        <DeleteConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeleteConfirm}
          title="Delete Price Entry"
          description={`Delete this price entry for "${deleteTarget?.item_name}"?`}
          itemName={deleteTarget?.item_name}
          isLoading={deleteItemPrice.isPending}
          destructive
        />
      </div>
    </SidebarInset>
  );
}
